import useVenuePortalUserOptions from "@/components/VenuePortalUserOptionsAutocomplete/useVenuePortalUserOptions";
import useUser from "@/hooks/useUser";
import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";

type VenuePortalUserOptionsAutocompleteProps = Omit<
  AutocompleteProps<
    ReturnType<
      typeof useVenuePortalUserOptions
    >["venuePortalUserOptions"][number],
    false,
    true,
    false
  >,
  "loading" | "value" | "options" | "onChange" | "renderInput"
> & { textFieldProps?: TextFieldProps; onChangeCallback?: () => void };
export default function VenuePortalUserOptionsAutocomplete({
  textFieldProps,
  onChangeCallback,
  ...props
}: VenuePortalUserOptionsAutocompleteProps) {
  const {
    venuePortalUserOptions,
    venuePortalUserOptionMap,
    getVenuePortalUsersResult: { loading },
  } = useVenuePortalUserOptions();

  const {
    user: { userId },
    impersonateUser,
  } = useUser();

  const value = venuePortalUserOptionMap.get(userId ?? "");

  return (
    <Autocomplete
      data-testid="venue-portal-user-autocomplete"
      {...props}
      loading={loading}
      value={value}
      options={venuePortalUserOptions}
      getOptionLabel={({ label }) => label ?? ""}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: React.Key };
        return option.label !== null ? (
          <li {...rest} key={key}>
            {option.label}
          </li>
        ) : null;
      }}
      onChange={(_, user) => {
        impersonateUser(user?.id);
        onChangeCallback?.();
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          disabled={loading || textFieldProps?.disabled}
          inputProps={{
            ...params.inputProps,
            ...textFieldProps?.inputProps,
            readOnly: loading || textFieldProps?.inputProps?.readOnly,
            value: loading ? "loading" : params.inputProps.value,
          }}
          InputProps={{
            ...params.InputProps,
            ...textFieldProps?.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
